import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
// eslint-disable-next-line import/extensions
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    marginTop: theme.spacing(0.5),
    position: 'relative',
    width: 310,
    overflow: 'hidden',
  },
  yearToggle: {
    position: 'absolute',
    top: 15,
    right: 70,
    zIndex: 2,
    padding: 4,
  },
  inputRoot: {
    height: 37.8,
    backgroundColor: 'white',
  },
  input: {
    height: 37.8,
    padding: '0 14px',
    boxSizing: 'border-box',
  },
}));

// eslint-disable-next-line react/prop-types
const MinimalToolbar = ({ openView, setOpenView }) => {
  const classes = useStyles();

  if (openView !== 'date') return null;

  return (
    <IconButton
      className={classes.yearToggle}
      size="small"
      onClick={() => setOpenView('year')}
    >
      <FontAwesomeIcon icon={faCaretDown} size="sm" />
    </IconButton>
  );
};

const Calendar = ({
  label,
  size,
  placeholder,
  value,
  setSelected,
  clearable,
  mode,
  minAllowed,
  maxAllowed,

  maxDate,
  disableFuture,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const isMonthMode = mode === 'month';

  const selectedDate =
    value && value.length
      ? moment(value, isMonthMode ? 'YYYY-MM-DD' : 'DD/MM/YYYY')
      : null;

  const resolvedMinDate = minAllowed
    ? moment(minAllowed, 'YYYY-MM-DD').startOf('month')
    : undefined;

  const resolvedMaxDate = maxAllowed
    ? moment(maxAllowed, 'YYYY-MM-DD').endOf('month')
    : undefined;

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          paddingLeft: '0.5em',
          paddingRight: '0.5em',
        }}
      >
        {label && (
          <div
            style={{
              marginBottom: '.28571429rem',
              fontWeight: 'bold',
              fontSize: 14,
            }}
          >
            {label}
          </div>
        )}

        <KeyboardDatePicker
          autoOk
          variant="inline"
          inputVariant="outlined"
          fullWidth
          size={size === 'small' ? 'small' : 'medium'}
          clearable={clearable}
          value={selectedDate}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          format={isMonthMode ? 'MM/YYYY' : 'DD/MM/YYYY'}
          views={isMonthMode ? ['year', 'month'] : ['year', 'month', 'date']}
          placeholder={placeholder}
          minDate={resolvedMinDate}
          maxDate={maxDate || resolvedMaxDate}
          disableFuture={disableFuture}
          onChange={(date) => {
            if (!date || !date.isValid()) {
              setSelected('');
              return;
            }

            if (isMonthMode) {
              setSelected(date.format('YYYY-MM-DD'));
            } else {
              setSelected(date.format('DD/MM/YYYY'));
            }
          }}
          ToolbarComponent={MinimalToolbar}
          PopoverProps={{
            disableRestoreFocus: true,
            onClickAway: () => setOpen(false),
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            classes: { paper: classes.popoverPaper },
          }}
          InputProps={{
            onClick: () => setOpen(true),
            classes: {
              root: classes.inputRoot,
              input: classes.input,
            },
          }}
        />
      </div>
    </MuiPickersUtilsProvider>
  );
};

Calendar.propTypes = {
  label: PropTypes.string,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  setSelected: PropTypes.func,
  clearable: PropTypes.bool,
  mode: PropTypes.oneOf(['date', 'month']),
  minAllowed: PropTypes.string,
  maxAllowed: PropTypes.string,

  // ✅ added
  maxDate: PropTypes.object,
  disableFuture: PropTypes.bool,
};

Calendar.defaultProps = {
  label: '',
  size: 'medium',
  placeholder: '',
  value: '',
  setSelected: () => {},
  clearable: true,
  mode: 'date',
  minAllowed: null,
  maxAllowed: null,
  maxDate: null,
  disableFuture: false,
};

export default Calendar;
